import React, { useState, useEffect } from 'react';
import { Clock, Save, AlertCircle, CheckCircle2, Calendar, Coffee, Moon, Sun, Plus, Trash2, Gift, UtensilsCrossed } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../config';
import './ScheduleAdmin.css';

const DEFAULT_SCHEDULE = [
    { day: 'Lunes', open: '09:00', close: '20:00', enabled: true },
    { day: 'Martes', open: '09:00', close: '20:00', enabled: true },
    { day: 'Miércoles', open: '09:00', close: '20:00', enabled: true },
    { day: 'Jueves', open: '09:00', close: '20:00', enabled: true },
    { day: 'Viernes', open: '09:00', close: '20:00', enabled: true },
    { day: 'Sábado', open: '09:00', close: '20:00', enabled: true },
    { day: 'Domingo', open: '00:00', close: '00:00', enabled: false },
];

const ScheduleAdmin = () => {
    const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingFetch, setLoadingFetch] = useState(true);
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            setLoadingFetch(false);
            return;
        }
        const fetchSchedule = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/admin/schedule`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    const weekly = data.weekly || (Array.isArray(data) ? data : []);
                    if (weekly.length >= 7) {
                        const sorted = [...weekly].sort((a, b) => a.day_of_week - b.day_of_week);
                        const mapped = sorted.map((d) => ({
                            day: d.day_name,
                            open: (d.open_time || '09:00').substring(0, 5),
                            close: (d.close_time || '20:00').substring(0, 5),
                            enabled: !!d.enabled,
                        }));
                        setSchedule(mapped);
                    }
                    if (data.holidays && Array.isArray(data.holidays)) {
                        setHolidays(data.holidays.map((h) => ({
                            id: h.id,
                            holiday_date: (h.holiday_date || '').substring(0, 10),
                            day_name: h.day_name || 'Festivo',
                            open: (h.open_time || '09:00').substring(0, 5),
                            close: (h.close_time || '20:00').substring(0, 5),
                            enabled: !!h.enabled,
                        })));
                    }
                }
            } catch (err) {
                console.error('Error cargando horarios:', err);
                setNotification({ type: 'error', message: 'No se pudieron cargar los horarios' });
                setTimeout(() => setNotification(null), 3000);
            } finally {
                setLoadingFetch(false);
            }
        };
        fetchSchedule();
    }, []);

    const handleToggleDay = (index) => {
        const newSchedule = [...schedule];
        newSchedule[index].enabled = !newSchedule[index].enabled;
        setSchedule(newSchedule);
    };

    const handleTimeChange = (index, field, value) => {
        const newSchedule = [...schedule];
        newSchedule[index][field] = value;
        setSchedule(newSchedule);
    };

    const addHoliday = () => {
        const next = new Date();
        next.setMonth(next.getMonth() + 1);
        setHolidays((prev) => [
            ...prev,
            {
                holiday_date: next.toISOString().slice(0, 10),
                day_name: 'Festivo',
                open: '09:00',
                close: '18:00',
                enabled: false,
            },
        ]);
    };

    const removeHoliday = (index) => {
        setHolidays((prev) => prev.filter((_, i) => i !== index));
    };

    const updateHoliday = (index, field, value) => {
        setHolidays((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const handleSave = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setNotification({ type: 'error', message: 'Debes iniciar sesión para guardar' });
            setTimeout(() => setNotification(null), 3000);
            return;
        }
        setLoading(true);
        setNotification(null);
        try {
            const schedulePayload = schedule.map((item, index) => ({
                day_of_week: index,
                day_name: item.day,
                open_time: item.open,
                close_time: item.close,
                enabled: item.enabled,
            }));
            const holidaysPayload = holidays.map((h) => ({
                holiday_date: h.holiday_date,
                day_name: h.day_name || 'Festivo',
                open_time: h.open,
                close_time: h.close,
                enabled: h.enabled,
            }));
            const res = await fetch(`${API_BASE_URL}/admin/schedule`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ schedule: schedulePayload, holidays: holidaysPayload }),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.detail || 'Error al guardar');
            }
            setNotification({ type: 'success', message: 'Horarios y festivos guardados correctamente' });
            setTimeout(() => setNotification(null), 3000);
        } catch (err) {
            setNotification({ type: 'error', message: err.message || 'Error al guardar horarios' });
            setTimeout(() => setNotification(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="schedule-admin-container">
            <header className="admin-tab-header">
                <div className="header-info">
                    <h1>Gestión de Horarios</h1>
                    <p>Configura los días y horas de atención al público de la barbería</p>
                </div>
                <button
                    className={`save-btn ${loading ? 'loading' : ''}`}
                    onClick={handleSave}
                    disabled={loading}
                >
                    {loading ? (
                        <div className="spinner-small"></div>
                    ) : (
                        <>
                            <Save size={18} />
                            Guardar Cambios
                        </>
                    )}
                </button>
            </header>

            <AnimatePresence>
                {notification && (
                    <motion.div
                        className={`notification ${notification.type}`}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                    >
                        {notification.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                        {notification.message}
                    </motion.div>
                )}
            </AnimatePresence>

            {loadingFetch && (
                <div className="schedule-loading-fetch" style={{ textAlign: 'center', padding: '1rem' }}>
                    <div className="spinner-small"></div>
                    <span style={{ marginLeft: '8px' }}>Cargando horarios...</span>
                </div>
            )}
            <div className="schedule-admin-content">
                <div className="schedule-card-main">
                    <div className="schedule-table-header">
                        <div className="col-day">Día de la Semana</div>
                        <div className="col-status">Estado</div>
                        <div className="col-times">Horario de Atención</div>
                    </div>

                    <div className="schedule-rows">
                        {schedule.map((item, index) => (
                            <motion.div
                                key={item.day}
                                className={`schedule-row-item ${!item.enabled ? 'disabled' : ''}`}
                                layout
                            >
                                <div className="col-day">
                                    <div className="day-icon">
                                        <Calendar size={18} />
                                    </div>
                                    <span className="day-name">{item.day}</span>
                                </div>

                                <div className="col-status">
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={item.enabled}
                                            onChange={() => handleToggleDay(index)}
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                    <span className={`status-label ${item.enabled ? 'open' : 'closed'}`}>
                                        {item.enabled ? 'Abierto' : 'Cerrado'}
                                    </span>
                                </div>

                                <div className="col-times">
                                    <div className="time-inputs">
                                        <div className="time-field">
                                            <Sun size={14} className="input-icon" />
                                            <input
                                                type="time"
                                                value={item.open}
                                                onChange={(e) => handleTimeChange(index, 'open', e.target.value)}
                                                title="Hora de apertura"
                                            />
                                        </div>
                                        <span className="time-separator">a</span>
                                        <div className="time-field">
                                            <Moon size={14} className="input-icon" />
                                            <input
                                                type="time"
                                                value={item.close}
                                                onChange={(e) => handleTimeChange(index, 'close', e.target.value)}
                                                title="Hora de cierre"
                                            />
                                        </div>
                                    </div>
                                    {!item.enabled && <div className="closed-overlay">Sin atención este día (puedes editar las horas para cuando lo actives)</div>}
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Festivos */}
                    <div className="schedule-festivos">
                        <div className="schedule-festivos-header">
                            <div className="schedule-festivos-title">
                                <Gift size={20} />
                                <span>Festivos</span>
                            </div>
                            <button type="button" className="schedule-festivos-add" onClick={addHoliday}>
                                <Plus size={18} />
                                Agregar festivo
                            </button>
                        </div>
                        <p className="schedule-festivos-desc">Agrega fechas específicas (ej. Navidad, Año Nuevo) con fecha y horario. Si está cerrado, no se agendarán citas ese día.</p>
                        {holidays.length === 0 ? (
                            <div className="schedule-festivos-empty">Aún no hay festivos. Haz clic en &quot;Agregar festivo&quot;.</div>
                        ) : (
                            <div className="schedule-festivos-rows">
                                {holidays.map((h, index) => (
                                    <motion.div key={index} className="schedule-festivos-row" layout>
                                        <div className="schedule-festivos-row-fields">
                                            <div className="festivo-field">
                                                <label>Fecha</label>
                                                <input
                                                    type="date"
                                                    value={h.holiday_date}
                                                    onChange={(e) => updateHoliday(index, 'holiday_date', e.target.value)}
                                                />
                                            </div>
                                            <div className="festivo-field festivo-name">
                                                <label>Nombre (opcional)</label>
                                                <input
                                                    type="text"
                                                    placeholder="Ej. Navidad"
                                                    value={h.day_name}
                                                    onChange={(e) => updateHoliday(index, 'day_name', e.target.value)}
                                                />
                                            </div>
                                            <div className="festivo-field festivo-time">
                                                <label>Apertura</label>
                                                <input
                                                    type="time"
                                                    value={h.open}
                                                    onChange={(e) => updateHoliday(index, 'open', e.target.value)}
                                                />
                                            </div>
                                            <div className="festivo-field festivo-time">
                                                <label>Cierre</label>
                                                <input
                                                    type="time"
                                                    value={h.close}
                                                    onChange={(e) => updateHoliday(index, 'close', e.target.value)}
                                                />
                                            </div>
                                            <div className="festivo-field festivo-toggle">
                                                <label>Estado</label>
                                                <label className="switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={h.enabled}
                                                        onChange={(e) => updateHoliday(index, 'enabled', e.target.checked)}
                                                    />
                                                    <span className="slider round"></span>
                                                </label>
                                                <span className={`status-label small ${h.enabled ? 'open' : 'closed'}`}>
                                                    {h.enabled ? 'Abierto' : 'Cerrado'}
                                                </span>
                                            </div>
                                        </div>
                                        <button type="button" className="schedule-festivos-remove" onClick={() => removeHoliday(index)} title="Eliminar festivo">
                                            <Trash2 size={18} />
                                        </button>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <aside className="schedule-tips">
                    <div className="tip-card">
                        <div className="tip-icon">
                            <Coffee size={24} />
                        </div>
                        <h3>Día de descanso</h3>
                        <p>Desactiva los días en los que la barbería no abre (como los Domingos) para que no se puedan agendar citas.</p>
                    </div>
                    <div className="tip-card">
                        <div className="tip-icon">
                            <Clock size={24} />
                        </div>
                        <h3>Jornada Continua</h3>
                        <p>Los horarios configurados aquí definen el rango total de visibilidad en la landing page pública.</p>
                    </div>
                    <div className="tip-card">
                        <div className="tip-icon">
                            <Gift size={24} />
                        </div>
                        <h3>Festivos</h3>
                        <p>Los festivos con fecha y hora se muestran en la landing. Si los marcas como cerrados, no se podrán agendar citas ese día.</p>
                    </div>
                    <div className="tip-card">
                        <div className="tip-icon">
                            <UtensilsCrossed size={24} />
                        </div>
                        <h3>Almuerzo</h3>
                        <p>Los horarios de almuerzo se configuran por barbero en la pestaña Barberos. Cada barbero puede tener sus propios horarios de descanso.</p>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default ScheduleAdmin;
